/**
 * Feriados nacionais brasileiros.
 *
 * Exporta funções para obter o conjunto de feriados de um ano e verificar
 * se uma data é feriado nacional.
 *
 * Apenas feriados NACIONAIS são considerados (Lei nº 9.093/1995 e
 * Decreto nº 9.007/2017). Feriados estaduais, municipais e pontos
 * facultativos não estão incluídos.
 *
 * A data da Páscoa é calculada pela fórmula de Gauss (algoritmo anônimo
 * publicado por Gauss em 1800, corrigido por Zeller).
 */

import type { LocalDate } from './canonical.types';

// =========================================================================
// Cálculo da Páscoa (fórmula de Gauss/Meeus–Jones–Butcher)
// =========================================================================

/**
 * Calcula a data da Páscoa para o ano gregoriano informado.
 * Retorna objeto { month: 1..12, day: 1..31 }.
 *
 * Algoritmo: Meeus–Jones–Butcher (anônimo, publicado por Meeus em
 * "Astronomical Algorithms", 1991). Válido para anos gregoriano.
 */
function easterDate(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

/**
 * Formata ano/mês/dia em LocalDate (AAAA-MM-DD).
 */
function toLocalDate(year: number, month: number, day: number): LocalDate {
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

/**
 * Adiciona `days` dias a uma data representada como { year, month, day }.
 * Retorna novo { year, month, day }.
 */
function addDays(
  year: number,
  month: number,
  day: number,
  days: number,
): { year: number; month: number; day: number } {
  const d = new Date(year, month - 1, day + days);
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  };
}

// =========================================================================
// Função principal
// =========================================================================

/**
 * Retorna o conjunto de feriados nacionais brasileiros para o ano informado,
 * no formato AAAA-MM-DD (LocalDate).
 *
 * Inclui:
 * - Confraternização Universal (01/01)
 * - Carnaval: segunda e terça antes da Quarta-feira de Cinzas
 *   (Quarta de Cinzas = 46 dias antes da Páscoa)
 * - Sexta-feira Santa (Good Friday: 2 dias antes da Páscoa)
 * - Tiradentes (21/04)
 * - Dia do Trabalho (01/05)
 * - Corpus Christi (60 dias após a Páscoa)
 * - Independência do Brasil (07/09)
 * - Nossa Senhora Aparecida / Padroeira do Brasil (12/10)
 * - Finados (02/11)
 * - Proclamação da República (15/11)
 * - Natal (25/12)
 */
export function nationalHolidays(year: number): Set<LocalDate> {
  const easter = easterDate(year);
  const ey = year;
  const em = easter.month;
  const ed = easter.day;

  // Quarta de Cinzas = 46 dias antes da Páscoa
  const ashWednesday = addDays(ey, em, ed, -46);

  // Carnaval: 2ª e 3ª antes da Quarta de Cinzas
  const carnivalMonday = addDays(
    ashWednesday.year,
    ashWednesday.month,
    ashWednesday.day,
    -2,
  );
  const carnivalTuesday = addDays(
    ashWednesday.year,
    ashWednesday.month,
    ashWednesday.day,
    -1,
  );

  // Sexta-feira Santa = 2 dias antes da Páscoa
  const goodFriday = addDays(ey, em, ed, -2);

  // Corpus Christi = 60 dias após a Páscoa
  const corpusChristi = addDays(ey, em, ed, 60);

  const holidays = new Set<LocalDate>([
    // Fixos
    toLocalDate(year, 1, 1),   // Confraternização Universal
    toLocalDate(year, 4, 21),  // Tiradentes
    toLocalDate(year, 5, 1),   // Dia do Trabalho
    toLocalDate(year, 9, 7),   // Independência do Brasil
    toLocalDate(year, 10, 12), // Nossa Senhora Aparecida
    toLocalDate(year, 11, 2),  // Finados
    toLocalDate(year, 11, 15), // Proclamação da República
    toLocalDate(year, 12, 25), // Natal
    // Móveis (dependem da Páscoa)
    toLocalDate(carnivalMonday.year, carnivalMonday.month, carnivalMonday.day),
    toLocalDate(carnivalTuesday.year, carnivalTuesday.month, carnivalTuesday.day),
    toLocalDate(goodFriday.year, goodFriday.month, goodFriday.day),
    toLocalDate(corpusChristi.year, corpusChristi.month, corpusChristi.day),
  ]);

  return holidays;
}

// Cache por ano para evitar recalcular
const _cache = new Map<number, Set<LocalDate>>();

function cachedNationalHolidays(year: number): Set<LocalDate> {
  let set = _cache.get(year);
  if (set === undefined) {
    set = nationalHolidays(year);
    _cache.set(year, set);
  }
  return set;
}

/**
 * Retorna true se a data (AAAA-MM-DD) é feriado nacional brasileiro.
 *
 * Apenas feriados nacionais são considerados. Feriados estaduais,
 * municipais e pontos facultativos NÃO estão incluídos.
 */
export function isNationalHoliday(date: LocalDate): boolean {
  const year = Number(date.slice(0, 4));
  if (Number.isNaN(year)) return false;
  return cachedNationalHolidays(year).has(date);
}
